import React from 'react'

import {useBunkPlans} from '../../../queries/useBunkPlans'
import {useBunks} from '../../../queries/useBunks'
import {useDivisions} from '../../../queries/useDivisions'
import {useGenders} from '../../../queries/useGenders'
import {useSegment} from '../../../queries/useSegment'
import {useSessions} from '../../../queries/useSessions'
import {useTerm} from '../../../hooks/useTerm'
import {useRequiredAuth} from '../../../stores/AuthStore'
import {CamperSegment, ResourceID} from '../../../types'
import {nameMapFrom} from '../../../utils'
import copy from '../copy.json'

interface Noun {
  singular: string
  plural: string
}

interface Dimension {
  ids: string[]
  names: Map<number, string>
  noun: Noun
}

const countLabel = (count: number, {singular, plural}: Noun): string =>
  `${count} ${count === 1 ? singular : plural}`

const isPending = (query: {
  isLoading: boolean
  hasNextPage?: boolean
}): boolean => query.isLoading || Boolean(query.hasNextPage)

/**
 * Resolves a micropost's custom segment into the filter names the author picked,
 * e.g. "Female, Juniors, First Semester", for display in the audience tooltip.
 * Returns null whenever there is nothing to show — no segment, a fetch still in
 * flight, a failed fetch, or a segment that narrowed nothing — and the caller
 * then renders the bare "Custom Audience" label with no tooltip.
 *
 * Every name is included: the list row shows only "Custom Audience", so there is
 * no width to fit and nothing has to be dropped.
 *
 * Only a dimension the author actually restricted appears: the picker stores an
 * empty array when every option in a dimension is selected, so a non-empty
 * array is always a genuine narrowing and there is no "all 40 sessions" case to
 * enumerate. Gender therefore shows up only when exactly one of the two is
 * chosen.
 *
 * Names come from the season the POST was written in, which since AB#25433 is
 * persisted on the record. Sessions, bunks and bunk plans are only queryable
 * per season, so reading them from today's season would leave an older post's
 * dimensions unnamed; that dimension then falls back to a count rather than
 * dropping out or printing a raw ID. A row predating AB#25433 has no season, so
 * it keeps the old behavior and reads the current one.
 *
 * The three season-scoped queries cache-key on `season`, so a list spanning
 * several seasons fetches one option-list set per distinct season rather than
 * refetching per post. Divisions and genders are not season-scoped and are
 * shared across the whole list.
 */
export const useSegmentAudienceSelections = (
  segmentID: ResourceID | null,
  seasonID: number | null,
): string | null => {
  const {client} = useRequiredAuth()
  const season = seasonID ?? client.currentSeasonID

  // Nothing is fetched until a post with a segment actually renders: react-query
  // runs a query only while at least one observer wants it, so a list with no
  // custom-segment posts issues no lookup requests at all.
  const enabled = Boolean(segmentID)

  const segmentQuery = useSegment({segmentID})
  const genderQuery = useGenders()
  const divisionsQuery = useDivisions({enabled})
  const sessionsQuery = useSessions({season, enabled})
  const bunkPlansQuery = useBunkPlans({season, enabled})
  const bunksQuery = useBunks({season, enabled})

  const bunkSingular = useTerm('bunk').singular().titleCase().value()
  const bunkPlural = useTerm('bunk').plural().titleCase().value()

  const lookupsPending = [
    divisionsQuery,
    sessionsQuery,
    bunkPlansQuery,
    bunksQuery,
  ].some(isPending)

  const segment = segmentQuery.data as CamperSegment | undefined

  return React.useMemo(() => {
    if (!segment || segmentQuery.isLoading || segmentQuery.isError) {
      return null
    }

    // Showing names before the option lists finish paging would show a count
    // and then flip to names — and would understate the audience in between.
    // No tooltip at all is better than one that changes under the cursor.
    if (lookupsPending) return null

    // Same order the compose picker lists them in, so the label reads the way
    // the author built it.
    //
    // `CamperSegment.programIDs` is deliberately absent: the micropost picker
    // never offers programs and `emptySegment` keeps the array empty, so a
    // micropost segment never carries one. If that changes, add a dimension
    // here (and `usePrograms` needs the same `enabled` gate) — until then an
    // unlabelled dimension would leave the label understating the audience.
    const dimensions: Dimension[] = [
      {
        ids: segment.genderIDs,
        names: new Map(
          genderQuery.data.map(({ID, name}) => [Number(ID), name]),
        ),
        noun: copy.audienceLabel.gender,
      },
      {
        ids: segment.divisionIDs,
        names: nameMapFrom(divisionsQuery.data?.pages),
        noun: copy.audienceLabel.division,
      },
      {
        ids: segment.sessionIDs,
        names: nameMapFrom(sessionsQuery.data?.pages),
        noun: copy.audienceLabel.session,
      },
      {
        ids: segment.bunkPlanIDs,
        names: nameMapFrom(bunkPlansQuery.data?.pages),
        noun: {
          singular: `${bunkSingular} ${copy.audienceLabel.bunkPlanSuffix.singular}`,
          plural: `${bunkSingular} ${copy.audienceLabel.bunkPlanSuffix.plural}`,
        },
      },
      {
        ids: segment.bunkIDs,
        names: nameMapFrom(bunksQuery.data?.pages),
        noun: {singular: bunkSingular, plural: bunkPlural},
      },
    ]

    const parts = dimensions.flatMap(({ids, names, noun}) => {
      if (ids.length === 0) return []

      const resolved = ids
        .map((id) => names.get(Number(id)))
        .filter((name): name is string => name !== undefined)

      return resolved.length === ids.length
        ? resolved
        : [countLabel(ids.length, noun)]
    })

    if (parts.length === 0) return null

    return parts.join(', ')
  }, [
    segment,
    segmentQuery.isLoading,
    segmentQuery.isError,
    lookupsPending,
    genderQuery.data,
    divisionsQuery.data,
    sessionsQuery.data,
    bunkPlansQuery.data,
    bunksQuery.data,
    bunkSingular,
    bunkPlural,
  ])
}
